"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

export interface TrustStripProps {
  /** Already-translated claims, in the order they should be read. */
  lines: string[];
}

/**
 * The reassurance claims as one line the customer can take in at a glance.
 *
 * Three stacked rows cost ~180px on a phone, above the price and the buy
 * button. Wrapping them saved the space but left two ragged lines. Moving
 * them was the next idea and is the wrong one: a marquee suits the reviews,
 * where there is always another card coming and dipping in is the point, but
 * these three are a checklist. Someone deciding whether to pay wants all of
 * them at once, not one at a time as they cycle past — and a second moving
 * strip directly above the review strip sets two animations competing beside
 * the CTA.
 *
 * So: one line, static, that scrolls under the thumb when the market's
 * wording runs long.
 */
export default function TrustStrip({ lines }: TrustStripProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);

  // The fade is an affordance, not decoration: it has to appear only when
  // something is actually hidden. Applied unconditionally it dimmed the last
  // word on desktop, where all three claims fit and nothing is cut off.
  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // Rotating the phone, or a longer claim arriving with a different
    // material, both change the answer.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, lines]);

  if (lines.length === 0) return null;

  const fade =
    "linear-gradient(to right, #000 85%, rgba(0, 0, 0, 0.3) 96%, transparent)";

  return (
    <Box
      ref={ref}
      sx={{
        overflowX: "auto",
        // A scrollbar would cost more height than the line itself.
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        ...(overflowing ? { maskImage: fade, WebkitMaskImage: fade } : null),
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          width: "max-content",
          // Centred when they fit; hard left when they do not, so the first
          // claim is never half off the left edge.
          mx: overflowing ? 0 : "auto",
          pr: overflowing ? 2 : 0,
        }}
      >
        {lines.map((line) => (
          <Stack
            key={line}
            direction="row"
            spacing={0.25}
            alignItems="center"
            sx={{ flex: "0 0 auto" }}
          >
            <CheckIcon color="success" sx={{ fontSize: 14 }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {line}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
